import React, { useEffect, useState, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { Dropdown } from 'primereact/dropdown';
import { Tooltip } from 'primereact/tooltip';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';

import LabelName from 'components/UI/LabelName/LabelName';
import AddLocationForm from './AddLocationForm';
import LocationSelect from 'components/UI/LocationSelect';
import { useConferenceRoomAvailability } from 'hooks/useConferenceRoomAvailability';

function EventLocation({ control, setValue, watch, editEventModal, eventDetails }) {
  const [addLocation, setAddLocation] = useState(false);

  // Watch form values
  const selectedLocation = watch('location');
  const meetingStartTime = watch('meeting_start_time');
  const meetingEndTime = watch('meeting_end_time');
  const currentConferenceRoomId = watch('conference_room_id');
  const whenValue = watch('when');
  const allDay = watch('all_day');

  // Check if selected location is an office location
  const isOfficeLocation = Boolean(selectedLocation?.office_location_id);

  // Use the availability hook
  const currentEventId = editEventModal && eventDetails?.pk ? eventDetails.pk : null;
  const { conferenceRooms, isLoading, error, refreshAvailability } = useConferenceRoomAvailability(
    isOfficeLocation ? selectedLocation?.value : null,
    meetingStartTime,
    meetingEndTime,
    {
      autoCheckAvailability: false,
      checkOnTimeChange: false,
      isAllDay: allDay,
    },
    currentEventId,
    whenValue
  );

  // Conference room options
  const conferenceRoomOptions = useMemo(() => {
    if (!editEventModal || !eventDetails) {
      return conferenceRooms;
    }

    const currentRoomId = eventDetails.conference_room_id || eventDetails.conference_room;
    if (!currentRoomId) {
      return conferenceRooms;
    }

    const roomExists = conferenceRooms.some((room) => room.value === currentRoomId);
    if (roomExists) {
      return conferenceRooms;
    }

    return [];
  }, [conferenceRooms, editEventModal, eventDetails]);

  useEffect(() => {
    if (!editEventModal && !isOfficeLocation && currentConferenceRoomId) {
      setValue('conference_room_id', '');
    }
  }, [selectedLocation, editEventModal, isOfficeLocation, currentConferenceRoomId, setValue]);

  return (
    <>
      <div className="d-flex py-2 flex-wrap">
        <div className="col-md-4 col-12 mt-2">
          <LabelName required={false} htmlFor="location">
            Location
          </LabelName>
        </div>
        <div className="col-md-7 col-11 align-items-center">
          {addLocation ? (
            <AddLocationForm setAddLocation={setAddLocation} setValue={setValue} />
          ) : (
            <Controller
              name="location"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <LocationSelect
                  id={field.name}
                  value={field.value}
                  onChange={(e) => field.onChange(e)}
                  placeholder="Select Location"
                  className="w-100"
                  isSearchable={true}
                />
              )}
            />
          )}
        </div>
        <div className="col-sm-1 d-flex justify-content-end mt-2 ps-2">
          <span className="pointer">
            <i className="fas fa-plus-circle location-add" onClick={() => setAddLocation(true)}></i>
            <Tooltip content="Add Location" position="top" target=".location-add" showDelay={500} />
          </span>
        </div>
      </div>

      {/* Video Conference URL */}
      <div className="d-flex align-items-center py-2 flex-wrap">
        <div className="col-md-4 col-12">
          <LabelName required={false} htmlFor="conference_url">
            Video Conference URL
          </LabelName>
        </div>
        <div className="col-md-8 col-12">
          <Controller
            name="conference_url"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <InputText
                type="text"
                id={field.name}
                {...field}
                className="w-100 input-shadow"
                placeholder="https://zoom.us/j/... or https://meet.google.com/..."
              />
            )}
          />
        </div>
      </div>

      {/* Conference Room */}
      {selectedLocation && isOfficeLocation && (
        <div className="d-flex py-2 flex-wrap">
          <div className="col-md-4 col-12 mt-2">
            <LabelName required={false} htmlFor="conference_room_id">
              Conference Room
            </LabelName>
          </div>
          <div className="col-md-8 col-12 align-items-center">
            <Controller
              name="conference_room_id"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <div className="position-relative">
                  <Dropdown
                    id={field.name}
                    value={field.value}
                    onChange={(e) => {
                      const selectedRoom = conferenceRoomOptions.find((room) => room.value === e.value);
                      if (selectedRoom && selectedRoom.disabled) {
                        return;
                      }
                      field.onChange(e.value);
                    }}
                    options={conferenceRoomOptions}
                    placeholder={isLoading ? 'Loading rooms...' : 'Select Conference Room'}
                    className="w-100"
                    filter
                    showClear={field.value ? true : false}
                    optionLabel="label"
                    optionValue="value"
                    disabled={isLoading}
                    optionDisabled="disabled"
                    itemTemplate={(option) => (
                      <div
                        style={{
                          color: option.disabled ? '#999' : 'inherit',
                          fontStyle: option.disabled ? 'italic' : 'normal',
                        }}
                      >
                        {option.dropdownLabel || option.label}
                      </div>
                    )}
                    onShow={() => {
                      if (isOfficeLocation && selectedLocation && meetingStartTime && meetingEndTime) {
                        refreshAvailability();
                      }
                    }}
                  />
                  {isLoading && (
                    <div className="position-absolute" style={{ right: '30px', top: '50%', transform: 'translateY(-50%)' }}>
                      <ProgressSpinner style={{ width: '20px', height: '20px' }} strokeWidth="4" />
                    </div>
                  )}
                </div>
              )}
            />
            {error && <small className="text-warning mt-1 d-block">{error}</small>}
          </div>
        </div>
      )}
    </>
  );
}

export default EventLocation;
